/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { AsyncPipe, CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  BaseSiteService,
  CmsAddToCartComponent,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  isNotNullable,
  Product,
  TranslatePipe,
  VariantOption,
} from '@spartacus/core';
import { CmsComponentData, CurrentProductService, ProductListItemContext } from '@spartacus/storefront';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AttractionSelectionDailogComponent } from 'src/app/udxb2b/dailog/AttractionSelectionDailog/attraction-selection-dailog-component';
import { UdxActiveCartFacade } from 'src/app/udxb2b/facade/udx-active-cart.facade';
import { Udxb2bCalendarFacade } from 'src/app/udxb2b/facade/udxb2b-calendar-facade';
import { UdxDataTransferService } from 'src/app/udxb2b/services/udx-data-transfer.service.service';
import { UDXAppUtils } from 'src/app/udxb2b/utilities/constants/udx-app.utils';
import { AttractionCardComponent } from '../../../dailog/AttractionSelectionDailog/AttractionCardComponent/attraction-card.component';
import { Udxb2bItemCounterComponent } from '../../item-counter/item-counter.component';
import { Udxb2bAttraction, Udxb2bAttractionAPICallData, Udxb2bAttractionEvents } from '../../model';
import { ExpressPassContext } from '../../model/udxb2b.enums';
import { UdxB2bProductSummaryComponent } from '../../product/product-summary/product-summary.component';
import { setExpressData } from '../../state/express-pass.actions';
import { ExpressPassState } from '../../state/express-pass.state';
import { Udxb2bAddToCartComponent } from '../add-to-cart.component';

@Component({
  selector: 'cx-add-to-cart',
  templateUrl: './expresspass-add-to-cart.component.html',
  styleUrls: ['./expresspass-add-to-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    UdxB2bProductSummaryComponent,
    Udxb2bItemCounterComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatDialogModule,
    AttractionCardComponent,
    MatGridListModule,
    NgClass,
    AsyncPipe,
    CurrencyPipe,
    TranslatePipe,
  ],
})
export class Udxb2bExpressPassAddToCartComponent extends Udxb2bAddToCartComponent implements OnDestroy, AfterViewInit {
  dataForAttractions: Udxb2bAttractionAPICallData = {};
  expressContext = ExpressPassContext.eExpressContextNone;
  expressPassStateSub$: Subscription = new Subscription();
  attractionSelectionDisabled = true;
  enableAPILoading = new BehaviorSubject(true);
  expressPassState: ExpressPassState = {
    selectedDate: '',
    quantity: 0,
    total: 0,
  };
  expressPassAPISub$: Subscription = new Subscription();

  constructor(
    udxb2bCalendarFacade: Udxb2bCalendarFacade,
    currentProductService: CurrentProductService,
    cd: ChangeDetectorRef,
    activeCartService: UdxActiveCartFacade,
    component: CmsComponentData<CmsAddToCartComponent>,
    eventService: EventService,
    dialog: MatDialog,
    dataTransfer: UdxDataTransferService,
    public datePipe: DatePipe,
    protected store: Store<{ expressPass: ExpressPassState }>,
    private _router: Router,
    globalMessageService: GlobalMessageService,
    _baseSiteService: BaseSiteService,
    @Optional() productListItemContext?: ProductListItemContext
  ) {
    super(
      udxb2bCalendarFacade,
      currentProductService,
      cd,
      activeCartService,
      component,
      eventService,
      dialog,
      dataTransfer,
      globalMessageService,
      _baseSiteService,
      productListItemContext
    );
    this.expressPassStateSub$ = this.store.select('expressPass').subscribe((res) => {
      this.expressPassState = res;
    });
  }
  ngAfterViewInit(): void {
    document
      .querySelector("input[formControlName='datePicker']")
      ?.closest('mat-form-field')
      ?.classList.add('placeholderBold');
  }
  override ngOnInit() {
    if (this.product) {
      this.productCode = this.product.code ?? '';
      this.setStockInfo(this.product);
      this.cd.markForCheck();
    } else if (this.productCode) {
      // force hasStock and quantity for the time being, as we do not have more info:
      this.quantity = 1;
      this.hasStock = true;
      this.cd.markForCheck();
    } else {
      this.expressPassAPISub$ = (
        this.productListItemContext ? this.productListItemContext.product$ : this.currentProductService.getProduct()
      )
        .pipe(filter(isNotNullable))
        .subscribe((product) => {
          this.productCode = product.code ?? '';
          this.product = product;
          this.validateIfRedirectionRequired(this.product);
          this.createFormControl();
          this.setStockInfo(product);
          this.loadContext();
          this.enableAPILoading.next(false);
          this.cd.markForCheck();
        });
    }
  }
  validateIfRedirectionRequired(product: Product) {
    if (
      this.product.baseProduct &&
      (this.expressPassState.quantity <= 0 ||
        this.expressPassState.selectedDate === '' ||
        isNaN(this.expressPassState.total))
    ) {
      let url: string = '/product/' + this.product.baseProduct + '/' + this.product.baseProduct + '/';
      this.globalMessageService.add({ key: 'pdp.expressPass.errorMessage' }, GlobalMessageType.MSG_TYPE_WARNING);
      this._router.navigate([url]);
    }
  }

  loadContext() {
    if (this.product.baseProduct) {
      // The attribute "baseProduct" exists in the variant PDP to identify the base product associated with the variant
      this.expressContext = ExpressPassContext.eExpressContextVariantProduct;
      this.isAddToCartDisabled = true; // In variant PDP quantity and date are non-editable and they are set, so the buttons need to be enabled
    } else {
      this.expressContext = ExpressPassContext.eExpressContextBaseProduct;
    }
    // USJT-9499: If the the attribute productVariantSelector is set to "MULTI_SELECT" then the express pass does not has any variant selection
    if (this.product.productVariantSelector === 'MULTI_SELECT') {
      // alert ("MULTI_SELECT");
      this.expressContext =
        ExpressPassContext.eExpressContextBaseProduct | ExpressPassContext.eExpressContextMainProduct;
      this.isAddToCartDisabled = false;
    }
  }

  public override set isAddToCartDisabled(newValue: boolean) {
    // In variant PDP quantity and date are non-editable and they are set, so the buttons need to be enabled
    this._isAddToCartDisabled = false;
  }

  createFormControl() {
    // alert ("createFormControl");
    const formGroupFields = this.getFormControlsFields();
    this.addToCartForm = new UntypedFormGroup(formGroupFields);
    if (this.addToCartForm.get('datePicker')?.value && this.expressPassState.selectedDate) {
      this.isDateSelected = true;
      // Need to call FetchCalendar API during first page load from EXPRESS PASS PLP to EXPRESS PASS BASE PRODUCT PDP
      this.onPickerToggle();
    }
  }
  override getVariantQuantity(option: VariantOption) {
    return this.addToCartForm.get('quantity')?.value;
  }
  override getFormControlsFields() {
    const formGroupFields = {} as string | any;
    const quantityBtn = this.stepperBtnDisabled(this.product.stock);
    formGroupFields['quantity'] = new UntypedFormControl({
      value:
        this.expressPassState.quantity > 0
          ? this.expressPassState.quantity
          : this.product.minQty
            ? this.product.minQty
            : 0,
      disabled: this.product.baseProduct ? true : quantityBtn,
    });
    formGroupFields['datePicker'] = new UntypedFormControl({
      value: this.expressPassState.selectedDate ? new Date(this.expressPassState.selectedDate) : '',
      disabled: true,
    }); // input feild is always disabled

    if (this.product.timeSelectionRequired) {
      formGroupFields['timeSlotPicker'] = new UntypedFormControl({ value: [''], disabled: true });
    }
    this.subtotal.next(this.expressPassState.total);
    return formGroupFields;
  }

  validateOutOfStock(stockStatus: string | undefined) {
    return stockStatus === 'outOfStock';
  }

  selectAttractions() {
    this.dataForAttractions = {};
    this.dataForAttractions = this.makeAttractionsAPIPayload();
    const dialogRef = this.dialog.open(AttractionSelectionDailogComponent, {
      data: this.dataForAttractions,
      height: '80%',
      width: '30%',
    });
    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.enableAPILoading.next(true);
          const date = new Date(this.addToCartForm.get('datePicker')?.value);
          let entryDate = this.datePipe.transform(date, 'yyyy-MM-dd');
          const data: ExpressPassState = {
            selectedDate: this.dataForAttractions.selectedDate!,
            quantity: Number(this.dataForAttractions.quantity),
            total:
              this.subtotal.value > 0
                ? this.subtotal.value
                : this.getPriceFormCalendarAPI(entryDate!, this.dataForAttractions),
          };
          const calculatedTotal =
            this.subtotal.value > 0
              ? this.subtotal.value
              : this.getPriceFormCalendarAPI(entryDate!, this.dataForAttractions);
          this.store.dispatch(setExpressData({ value: data }));
          let url: string = '/product/' + result + '/' + result + '/';
          this._router.navigate([url]);
        }
      })
    );
  }
  preparePayloadForExpressContextMainProduct() {
    const date = new Date(this.addToCartForm.get('datePicker')?.value);
    let entryDate = this.datePipe.transform(date, 'yyyy-MM-dd');
    const data: ExpressPassState = {
      selectedDate: this.dataForAttractions.selectedDate!,
      quantity: Number(this.dataForAttractions.quantity),
      total:
        this.subtotal.value > 0
          ? this.subtotal.value
          : this.getPriceFormCalendarAPI(entryDate!, this.dataForAttractions),
    };
    this.store.dispatch(setExpressData({ value: data }));
  }
  override addToCart(shouldDisableDate = true) {
    if (this.isExpressContextMainProduct()) {
      // this.preparePayloadForExpressContextMainProduct();
    }
    // alert ("dateSelectionRequired: " + this.product.dateSelectionRequired);
    super.addToCart(false);
  }
  getPriceFormCalendarAPI(entryDate: string, data: Udxb2bAttractionAPICallData): number {
    const calendarDates = this.availableTimeSlotAndPrice?.get(data.baseCode);
    const pricing = calendarDates
      .filter((cal: { date: string }) => cal.date === entryDate)
      .flatMap((obj: { pricing: [] }) => obj.pricing);
    return pricing[0]?.amount * Number(data.quantity);
  }

  makeAttractionsAPIPayload(): Udxb2bAttractionAPICallData {
    const data: Udxb2bAttractionAPICallData = {};
    if (ExpressPassContext.eExpressContextBaseProduct == this.expressContext) {
      data.baseCode = this.productCode;
    } else {
      data.baseCode = this.product.baseProduct;
    }
    data.quantity = this.addToCartForm.get('quantity')?.value;
    const date = new Date(this.addToCartForm.get('datePicker')?.value);
    let formattedDate = this.datePipe.transform(date, 'MM/dd/yyyy');
    data.selectedDate = formattedDate?.toString();
    if (ExpressPassContext.eExpressContextVariantProduct == this.expressContext) {
      data.variantSelectedCode = this.productCode;
    }
    return data;
  }

  isExpressContextVariantProduct() {
    return this.expressContext & ExpressPassContext.eExpressContextVariantProduct;
  }
  isExpressContextMainProduct() {
    return this.expressContext & ExpressPassContext.eExpressContextMainProduct;
  }
  isExpressContextBaseProduct() {
    return this.expressContext & ExpressPassContext.eExpressContextBaseProduct;
  }
  getAttractionsForKey(inKey: string, inSubgroup?: string): Udxb2bAttraction[] {
    let attraction: Udxb2bAttraction = {
      attractions: this.getAttractionEventsForKey(inKey, inSubgroup),
      selected: false,
    };
    return [attraction];
  }
  getCountOfAttractionEventsForKey(inKey: string, inSubgroup?: string): number {
    let count = this.getAttractionEventsForKey(inKey, inSubgroup, false).events?.length;
    return count ?? 0;
  }
  getAttractionEventsForKey(inKey: string, inSubgroup?: string, shouldSort = true): Udxb2bAttractionEvents {
    let attractionEvents: Udxb2bAttractionEvents = {
      events: [],
    };
    let filteredEvents = this.product.attractions?.events?.filter((anEvent) => {
      var qualified = false;
      if (anEvent.maingroup === inKey) {
        if (inSubgroup) {
          // Check this condition if only subGroup info is passed to this method
          if (anEvent.subgroup === inSubgroup) {
            qualified = true;
          }
        } else {
          qualified = true;
        }
      }
      return qualified;
    });
    if (shouldSort) {
      attractionEvents.events = UDXAppUtils.sortEventsByStartTimeAndLandEntry(filteredEvents!);
    } else {
      attractionEvents.events = filteredEvents;
    }
    return attractionEvents;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.expressPassStateSub$) {
      this.expressPassStateSub$.unsubscribe();
    }
    if (this.expressPassAPISub$) {
      this.expressPassAPISub$.unsubscribe();
    }
  }

  override onQuantityChanged(quantity: number, hideOnCart: boolean | undefined): void {
    if (hideOnCart != undefined && hideOnCart) {
      this.attractionSelectionDisabled = true;
      this.globalMessageService.add({ key: 'pdp.hideOnCart_warning' }, GlobalMessageType.MSG_TYPE_WARNING);
    } else {
      this.addToCartForm.get('datePicker')?.reset();
      this.attractionSelectionDisabled = true;
      document
        .querySelector("input[formControlName='datePicker']")
        ?.closest('mat-form-field')
        ?.classList.add('placeholderBold');
    }
  }

  override dateSelected(date: MatDatepickerInputEvent<Date>) {
    super.dateSelected(date);
    if (date.value) {
      this.attractionSelectionDisabled = false;
    }
  }

  disableDateSelection() {
    if (this.product.hideOnCart) {
      this.attractionSelectionDisabled = true;
      this.globalMessageService.add({ key: 'pdp.hideOnCart_warning' }, GlobalMessageType.MSG_TYPE_WARNING);
      return true;
    }
    if (!this.isExpressContextBaseProduct() && !this.isExpressContextMainProduct) {
      return true;
    }
    if (this.addToCartForm.get('quantity')?.value) {
      this.attractionSelectionDisabled = this.addToCartForm.get('datePicker')?.value ? false : true;
      return false;
    }
    return true;
  }

  override onPickerToggle() {
    // Fire Date selection only for Base Product.
    if (!this.product.baseProduct) {
      this.isDateSelectionDisabled = false;
    } else {
      this.isDateSelectionDisabled = true;
    }
    super.onPickerToggle();
  }
}
